/** @format */

import { pickGa4Params } from 'src/analytics/ga4Parameters';
import type {
  Ga4BaseParams,
  Ga4ComponentInteractionParams,
  Ga4DocumentDownloadParams,
  Ga4SearchNoResultsParams,
  Ga4SearchParams,
  Ga4SearchResultClickParams,
  Ga4WidgetClickParams
} from 'src/analytics/ga4Parameters';

export function getFileExtensionFromUrl(nameOrUrl?: string | null): string | undefined {
  if (!nameOrUrl || typeof nameOrUrl !== 'string') {
    return undefined;
  }

  const withoutQuery = nameOrUrl.split('?')[0].split('#')[0];
  const lastSegment = withoutQuery.split('/').pop() || withoutQuery;
  const dotIndex = lastSegment.lastIndexOf('.');

  if (dotIndex === -1 || dotIndex === lastSegment.length - 1) {
    return undefined;
  }

  return lastSegment.slice(dotIndex + 1).toLowerCase();
}

export function buildWidgetClickParams(params: Ga4WidgetClickParams = {}): Ga4WidgetClickParams {
  return {
    widget_name: params.widget_name,
    widget_position: params.widget_position,
    widget_category: params.widget_category
  };
}

export function buildComponentInteractionParams(
  params: Ga4ComponentInteractionParams = {}
): Ga4ComponentInteractionParams {
  return {
    component_name: params.component_name,
    component_type: params.component_type,
    component_position: params.component_position
  };
}

export function buildDocumentDownloadParams(
  params: Ga4DocumentDownloadParams & { url?: string; file_name?: string } = {}
): Ga4DocumentDownloadParams {
  const documentName = params.document_name || params.file_name;
  const documentType = params.document_type || getFileExtensionFromUrl(params.url || documentName);

  return {
    document_name: documentName,
    document_type: documentType,
    document_category: params.document_category
  };
}

export function buildSearchParams(params: Ga4SearchParams = {}): Ga4SearchParams {
  return {
    search_term: params.search_term,
    results_count: params.results_count,
    search_location: params.search_location
  };
}

export function buildSearchNoResultsParams(params: Ga4SearchNoResultsParams = {}): Ga4SearchNoResultsParams {
  return {
    search_term: params.search_term,
    search_location: params.search_location
  };
}

export function buildSearchResultClickParams(params: Ga4SearchResultClickParams = {}): Ga4SearchResultClickParams {
  return {
    search_term: params.search_term,
    search_location: params.search_location,
    content_id: params.content_id != null ? String(params.content_id) : undefined,
    result_position: params.result_position
  };
}

export interface BuildGa4EventPayloadOptions {
  eventParams?: Record<string, unknown>;
  pageContext?: Ga4BaseParams;
  userContext?: { user_status?: string; user_id?: string };
  includeBase?: boolean;
}

export function buildGa4EventPayload(
  eventKey: string,
  { eventParams = {}, pageContext = {}, userContext = {}, includeBase = true }: BuildGa4EventPayloadOptions = {}
): Record<string, string | number> {
  const baseParams = includeBase ? { ...pageContext, ...userContext } : {};
  const merged = {
    ...baseParams,
    event_name: eventKey,
    ...eventParams
  };

  return pickGa4Params(eventKey, merged);
}
